/**
 * Environment Detection
 *
 * Browser / webview probing used to decide Web3Auth's popup vs. redirect UX
 * mode, and whether the SDK is constructed at all — embedded contexts
 * (iframe or in-app webview) skip Web3Auth entirely and authenticate through
 * the session bridge instead (see `web3auth.gateway.ts`).
 *
 * Moved verbatim from `web3auth.gateway.ts` — every predicate below is
 * byte-for-byte the original logic. Do not "simplify" it: the
 * `'standalone' in navigator` check and the `Line\/` check each cover a
 * distinct device/browser that no other clause catches (see story 32-02).
 */

export interface DetectedEnvironment {
  isSafari: boolean;
  isMobileSafari: boolean;
  isMobile: boolean;
  isInIframe: boolean;
  isInWebView: boolean;
  isEmbedded: boolean;
  supportsPopupUxMode: boolean;
  uxMode: 'popup' | 'redirect';
}

export function detectEnvironment(): DetectedEnvironment {
  // Detect Safari browsers for UX mode selection
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMobileSafari = /iP(ad|od|hone)/i.test(navigator.userAgent) && isSafari;

  // A mobile popup is a real tab: Facebook's hand-off to its native app closes it (or the OS
  // discards it) before the result arrives, failing the login with "popup closed by the user".
  const isMobile = /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent);

  // Detect if running in iframe (cross-origin iframes throw on access)
  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  // Detect native webviews and in-app browsers
  // Popups are blocked in these contexts, and Google blocks OAuth from embedded webviews
  const isInWebView = (() => {
    const ua = navigator.userAgent;

    // Android WebView includes "wv" in user agent
    if (/(wv|WebView)/i.test(ua)) return true;

    // In-app browsers (Facebook, Instagram, TikTok, Line)
    if (/FBAN|FBAV|Instagram|TikTok|Line\//i.test(ua)) return true;

    // iOS WKWebView: has webkit messageHandlers but doesn't identify as Safari
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).webkit &&
      ((window as unknown as Record<string, unknown>).webkit as Record<string, unknown>)
        .messageHandlers &&
      !/Safari/i.test(ua)
    )
      return true;

    // iOS fallback: iOS device without 'standalone' property indicates WKWebView
    const isIOS = /iP(ad|od|hone)/i.test(ua);
    if (isIOS && !('standalone' in navigator)) return true;

    return false;
  })();

  const isEmbedded = isInIframe || isInWebView;
  const supportsPopupUxMode = !isMobileSafari && !isSafari && !isEmbedded && !isMobile;

  return {
    isSafari,
    isMobileSafari,
    isMobile,
    isInIframe,
    isInWebView,
    isEmbedded,
    supportsPopupUxMode,
    uxMode: supportsPopupUxMode ? 'popup' : 'redirect'
  };
}

// Evaluate once at module load — the gateway and the ViewModel read these
// constants (never call detectEnvironment() again) so the SDK-construction
// decision cannot drift mid-session.
const environment = detectEnvironment();

/** Whether the app is running in an embedded context (iframe or webview) */
export const isEmbedded = environment.isEmbedded;

export const isBrowserSupportingPopupUxMode = environment.supportsPopupUxMode;
